import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { get } from '@/features/shared/lib/api';
import { arr, parsePayload, buildListParams } from '@/features/shared/lib/utils';
import { useLocation, useNavigationType } from 'react-router-dom';
import { FacetBlock, RangeBlock } from "@/features/shared/components/Facet";
import JournalCard from '@/features/journal/components/JournalCard';
import JournalSortSelect from '@/features/journal/components/JournalSortSelect';
import JournalQuartiles from '@/features/journal/components/charts/JournalQuartiles';
import { TopCategoriesByDB } from '@/features/journal/components/charts/TopCategoriesByDB';
import Paginator from "@/features/shared/ui/Paginator";
import { FacetPopup } from "@/features/shared/components/FacetPopup";
import JournalsFilterSidebar from '@/features/journal/components/JournalsFilterSidebar';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function Journals() {
  // Location & navigation type should be available early for cache scoping and hydration decisions
  const location = useLocation();
  const navType = useNavigationType();
  const hydratedRef = useRef(false);
  const cacheKey = useMemo(() => {
    try {
      const { pathname, search } = window.location;
      return `JOURNALS_CACHE::${pathname}${search}`;
    } catch (e) {
      return 'JOURNALS_CACHE';
    }
  }, [location]);
  const loadCache = () => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveCache = (payload) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (e) {
    }
  };
  const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
  const isValidCache = (c) => {
    if (!c) return false;
    if (!Array.isArray(c.rows) || c.rows.length === 0) return false;
    if (typeof c.ts !== 'number') return true; // no ts means older code; treat as valid
    return (Date.now() - c.ts) <= CACHE_TTL_MS;
  };
  // Detect real browser back/forward navigation (not initial load)
  const isBackForwardNav = () => {
    try {
      const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navs && navs.length) {
        const type = navs[0]?.type;
        if (type === 'back_forward') return true;
        if (type === 'reload') return false; // explicit reload is not back/forward
        // 'navigate' is a normal fresh navigation
        return false;
      }
    } catch (e) {
    }
    // Fallback: react-router sets history.state.idx (>0 means not the first entry)
    try {
      const idx = window.history && window.history.state && window.history.state.idx;
      // This fallback can misclassify reload; be conservative and return true only if idx>0 and document.referrer matches same origin
      const sameOriginReferrer = document.referrer && document.referrer.startsWith(window.location.origin);
      if (typeof idx === 'number' && idx > 0 && sameOriginReferrer) return true;
    } catch (e) {
    }
    return false;
  };

  // If returning via back/forward and have a valid cache for this URL, pre-hydrate initial states to avoid empty flash
  const initialCacheRaw = (navType === 'POP' && isBackForwardNav()) ? loadCache() : null;
  const initialCache = isValidCache(initialCacheRaw) ? initialCacheRaw : null;

  const [rows, setRows] = useState(Array.isArray(initialCache?.rows) ? initialCache.rows : []);
  const [pageInfo, setPageInfo] = useState(initialCache?.pageInfo || { fromPage: 1, totalPage: 1, resultsPerPage: 20, totalResults: 0 });
  const [facet, setFacet] = useState(initialCache?.facet || {});
  // 초기 로딩 상태: 캐시가 없으면 true, 있으면 false
  const [loading, setLoading] = useState(!initialCache);
  const [err, setErr] = useState(null);
  // 첫 로딩인지 추적 (첫 API 호출일 때만 로딩 컴포넌트 표시)
  const isFirstLoadRef = useRef(!initialCache);

  // filters: { [facetKey]: string[] } - applied filters
  const [filters, setFilters] = useState(initialCache?.filters || {});
  // pendingFilters: staged selections applied on Refine
  const [pendingFilters, setPendingFilters] = useState(initialCache?.pendingFilters || {});
  // conjunctions: { [facetKey]: 'and' | null } - conjunction state for facets
  const [conjunctions, setConjunctions] = useState({});
  // sort: { order_by: string, order: 'asc'|'desc' }
  const [sort, setSort] = useState(initialCache?.sort || { order_by: 'ranking', order: 'asc' });
  const [popup, setPopup] = useState({ open: false, key: '', title: '' });
  // free-text query for search within results (API param: sub_ks)
  const [query, setQuery] = useState(typeof initialCache?.query === 'string' ? initialCache.query : '');
  // graph toggle state
  const [graphExpanded, setGraphExpanded] = useState(true);

  // URL에 검색 또는 패싯 관련 파라미터가 있으면 그래프 접기
  const checkIfSearchOrFacetMode = () => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('sub_ks')) return true;
      let hasFilter = false;
      let hasConjunction = false;
      sp.forEach((_, k) => {
        if (k.endsWith('[]')) hasFilter = true;
        if (k.endsWith('_conjunction')) hasConjunction = true;
      });
      if (hasFilter || hasConjunction) return true;
      if (Number(sp.get('current_page')) > 1) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  // Mark hydrated and restore scroll asap when coming back
  useEffect(() => {
    if (isValidCache(initialCache)) {
      hydratedRef.current = true;
      setTimeout(() => {
        try {
          window.scrollTo({ top: initialCache.scrollY || 0, left: 0, behavior: 'auto' });
        } catch (e) { }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildExtraParams = () => {
    const extra = {};
    // facet arrays as key[]=value or min_/max_ keys
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (k.startsWith('min_') || k.startsWith('max_')) {
        extra[k] = v;
      } else if (Array.isArray(v) && v.length) {
        extra[`${k}[]`] = v;
      }
    });
    // conjunction parameters
    Object.entries(conjunctions || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (sort?.order_by) extra.order_by = sort.order_by;
    if (sort?.order) extra.order = sort.order;
    // free text query (API expects sub_ks)
    if (String(query || '').trim()) extra.sub_ks = String(query).trim();
    return extra;
  };

  const cleanFilters = (f = {}) => {
    const out = {};
    Object.entries(f).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) out[k] = vals;
    });
    return out;
  };

  const fetchList = async (page = 1, opt = {}) => {
    // 첫 로딩일 때만 loading 상태를 true로 설정
    if (isFirstLoadRef.current) {
      setLoading(true);
    }
    setErr(null);
    try {
      const extra = opt.extra ? opt.extra : buildExtraParams();
      const params = buildListParams({ page, perPage: pageInfo.resultsPerPage || 20, extra });
      const data = await get('/rm/v1/journals', { params });
      const { status, records, pageInfo: pi, facet: fc } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      const nextRows = records;
      const nextPageInfo = {
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.record_per_page ?? 20,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      };
      const nextFacet = fc || {};
      setRows(nextRows);
      setPageInfo(nextPageInfo);
      setFacet(nextFacet);
      // save cache after successful fetch
      saveCache({
        rows: nextRows,
        pageInfo: nextPageInfo,
        facet: nextFacet,
        filters,
        pendingFilters,
        conjunctions,
        sort,
        query,
        scrollY: window.scrollY || 0,
        ts: Date.now(),
      });
    } catch (e) {
      console.error('[JOURNALS][fetchList]', e);
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false; // 첫 로딩 완료 후 플래그 해제
      }
    }
  };

  // URL 동기화 유틸
  const syncUrl = ({ page, filters: f = filters, sort: s = sort, conjunctions: c = {} } = {}) => {
    const sp = new URLSearchParams(window.location.search);
    // page
    if (page) sp.set('current_page', String(page)); else sp.delete('current_page');
    // sort
    if (s?.order_by) sp.set('order_by', s.order_by); else sp.delete('order_by');
    if (s?.order) sp.set('order', s.order); else sp.delete('order');
    // free text query (API & URL param: sub_ks)
    const qv = String(query || '').trim();
    if (qv) sp.set('sub_ks', qv); else sp.delete('sub_ks');
    // clear existing facet [] keys, conjunction keys, and min_/max_ keys
    const toDelete = [];
    sp.forEach((_, k) => {
      if (k.endsWith('[]')) toDelete.push(k);
      if (k.endsWith('_conjunction')) toDelete.push(k);
      if (k.startsWith('min_') || k.startsWith('max_')) toDelete.push(k);
    });
    toDelete.forEach(k => sp.delete(k));
    // add current filters as key[]=value or min_/max_ keys
    Object.entries(f || {}).forEach(([k, v]) => {
      if (k.startsWith('min_') || k.startsWith('max_')) {
        sp.set(k, String(v));
      } else if (Array.isArray(v)) {
        v.forEach(val => sp.append(`${k}[]`, val));
      }
    });
    // add conjunction parameters
    Object.entries(c || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        sp.set(`${k}_conjunction`, 'and');
      }
    });
    const newUrl = `${window.location.pathname}?${sp.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  useLayoutEffect(() => {
    // 뒤로가기(POP)로 들어왔고, 아직 한 번도 hydrate 안 했을 때만 시도
    if (navType !== 'POP' || hydratedRef.current) return;

    const cached = loadCache();
    if (isValidCache(cached)) {
      hydratedRef.current = true;

      setRows(cached.rows || []);
      setPageInfo(cached.pageInfo || pageInfo);
      setFacet(cached.facet || {});
      if (cached.sort) setSort(cached.sort);
      if (cached.filters) {
        setFilters(cached.filters);
        setPendingFilters(cached.pendingFilters || cached.filters);
      }
      if (cached.conjunctions) {
        setConjunctions(cached.conjunctions);
      }
      if (typeof cached.query === 'string') setQuery(cached.query);

      // 스크롤 복원도 여기서 바로 처리 (setTimeout 불필요)
      try {
        window.scrollTo({
          top: cached.scrollY || 0,
          left: 0,
          behavior: 'auto',
        });
      } catch (e) { }
    }
  }, [navType]);

  useEffect(() => {
    // Hydrate only on real browser back/forward; if valid cache exists, use it and skip initial fetch
    if (navType === 'POP' && isBackForwardNav()) {
      const cached = loadCache();
      if (isValidCache(cached)) {
        hydratedRef.current = true;
        // If pre-fill didn't run for any reason, set now
        if (!rows || rows.length === 0) {
          setRows(cached.rows || []);
          setPageInfo(cached.pageInfo || pageInfo);
          setFacet(cached.facet || {});
          if (cached.sort) setSort(cached.sort);
          if (cached.filters) setFilters(cached.filters);
          if (cached.pendingFilters) setPendingFilters(cached.pendingFilters);
          if (cached.conjunctions) setConjunctions(cached.conjunctions);
          if (typeof cached.query === 'string') setQuery(cached.query);
        }
        setTimeout(() => {
          try {
            window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' });
          } catch (e) {
          }
        }, 0);
        return; // skip initial fetch
      }
    }

    const sp = new URLSearchParams(window.location.search);
    const initPage = Number(sp.get('current_page')) || 1;
    const hasKeyword = !!(sp.get('ks') || sp.get('sub_ks'));
    const initSort = { order_by: sp.get('order_by') || (hasKeyword ? 'score' : 'ranking'), order: sp.get('order') || 'asc' };
    const initFilters = {};
    const initConjunctions = {};
    // collect keys ending with [] and min_/max_ keys
    sp.forEach((v, k) => {
      if (k.endsWith('[]')) {
        const base = k.slice(0, -2);
        const all = sp.getAll(k);
        initFilters[base] = Array.from(new Set(all));
      } else if (k.startsWith('min_') || k.startsWith('max_')) {
        initFilters[k] = Number(v) || 0;
      } else if (k.endsWith('_conjunction')) {
        const base = k.replace('_conjunction', '');
        if (v === 'and') {
          initConjunctions[base] = 'and';
        }
      }
    });
    const initQuery = sp.get('sub_ks') || '';
    setQuery(initQuery);
    setSort(initSort);
    setFilters(initFilters);
    setPendingFilters(initFilters);
    setConjunctions(initConjunctions);
    // Build extra for first fetch to avoid race with setState
    const extra = {};
    Object.entries(initFilters).forEach(([k, v]) => {
      if (k.startsWith('min_') || k.startsWith('max_')) {
        extra[k] = v;
      } else if (Array.isArray(v) && v.length) {
        extra[`${k}[]`] = v;
      }
    });
    Object.entries(initConjunctions).forEach(([k, v]) => {
      if (v === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    if (initSort.order_by) extra.order_by = initSort.order_by;
    if (initSort.order) extra.order = initSort.order;
    if (String(initQuery || '').trim()) extra.sub_ks = String(initQuery).trim();
    if (checkIfSearchOrFacetMode()) {
      setGraphExpanded(false);
    }
    fetchList(initPage, { extra });
  }, [navType]);

  // URL에 검색·패싯 관련 파라미터가 있으면 그래프 접기 (URL 변경 시마다 체크)
  useEffect(() => {
    if (checkIfSearchOrFacetMode()) {
      setGraphExpanded(false);
    }
  }, [location.search]);

  // keep scroll position in cache
  useEffect(() => {
    const onScroll = () => {
      const cached = loadCache();
      const now = Date.now();
      // If we currently have real rows, write/update full cache; otherwise only update scroll if a valid cache already exists
      if (Array.isArray(rows) && rows.length > 0) {
        const next = {
          rows,
          pageInfo,
          facet,
          filters,
          pendingFilters,
          conjunctions,
          sort,
          scrollY: window.scrollY || 0,
          ts: now,
        };
        saveCache(next);
      } else if (isValidCache(cached)) {
        cached.scrollY = window.scrollY || 0;
        cached.ts = now;
        saveCache(cached);
      } // else: do nothing, avoid creating empty cache
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [rows, pageInfo, facet, filters, pendingFilters, sort]);

  const onPage = (p) => {
    if (!p || p === pageInfo.fromPage) return;
    syncUrl({ page: p });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
      // no-op
    }
    fetchList(p);
  };

  // trigger search using current query and filters, resets to page 1
  const doSearch = () => {
    syncUrl({ page: 1, conjunctions });
    setGraphExpanded(false); // 검색 시 그래프 접기 (replaceState는 location을 갱신하지 않으므로 여기서 처리)
    const scoreSort = { order_by: 'score', order: 'desc' };
    setSort(scoreSort);
    syncUrl({ page: 1, conjunctions, sort: scoreSort });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
    }
    const extra = buildExtraParams();
    extra.order_by = 'score';
    extra.order = 'desc';
    fetchList(1);
  };

  if (err) return <div className="p-4 text-red-600">에러: {String(err)}</div>;

  return (
    <div className="journallistPage listPage flexPage mt-[127px] lg:mt-[120px] px-6 2xl:px-[100px]">
      <JournalsFilterSidebar
        facet={facet}
        filters={filters}
        setFilters={setFilters}
        pendingFilters={pendingFilters}
        setPendingFilters={setPendingFilters}
        conjunctions={conjunctions}
        setConjunctions={setConjunctions}
        sort={sort}
        query={query}
        setQuery={setQuery}
        doSearch={doSearch}
        syncUrl={syncUrl}
        fetchList={fetchList}
        cleanFilters={cleanFilters}
        setPopup={setPopup}
        setGraphExpanded={setGraphExpanded}
      />

      <div className="journal-list-cont w-full pt-[30px] min-[800px]:w-[calc(100%-360px)] min-[800px]:pt-[23px] min-[800px]:mt-[50px]">
        <PageBreadcrumb current="Journals" />

        <div className="tit-area w-full flex justify-between">
          <h2 className="text-[26px] leading-[130%] font-black min-[800px]:text-[32px]">
            Journals
          </h2>
          <div className="flex items-center gap-2 shrink-0">

            <button className="btn-filter flex items-center justify-center w-[38px] h-[38px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:hidden">
              <img
                src={`${rmVars.themeUrl}images/filter-ico.png`}
                className="w-[20px]"
                alt="filter"
              />
            </button>
          </div>
        </div>

        <div className="journal-graph mt-[18px] py-[24px] bg-white rounded-xl border border-[#E4E8F1] min-[800px]:mt-[37px] min-[800px]:pt-[20px] min-[800px]:pb-[20px]">
          <div
            className="journal-graph-toggle relative px-[20px] cursor-pointer min-[800px]:px-[25px]"
            onClick={() => setGraphExpanded(!graphExpanded)}
          >
            <div className="flex items-center gap-3 pr-[30px] text-[15px] font-bold leading-[130%] min-[800px]:text-[18px] min-[800px]:gap-[6px]">
              <div className="flex shrink-0 justify-center items-center w-[38px] h-[38px] rounded-lg bg-[#E4E8F1] min-[800px]:w-auto min-[800px]:h-auto min-[800px]:bg-transparent">
                <img src={`${rmVars.themeUrl}images/bar_chart_ico.png`} className="w-[22px]" alt="chart icon" />
              </div>
              Exploring Our Journal Publishing Trends
            </div>
            <img
              src={`${rmVars.themeUrl}images/expand_more_333.png`}
              className={`absolute right-[20px] top-1/2 w-[20px] -translate-y-1/2 transition-transform duration-300 min-[800px]:w-[24px] ${graphExpanded ? 'rotate-180' : ''}`}
              alt="toggle"
            />
          </div>

          {graphExpanded && (
            <div className="journal-graph-cont font-[roboto] border-t border-t-[#E4E8F1] mt-6 pt-5 px-[20px] min-[800px]:mt-5 min-[800px]:pt-[32px] flex flex-col xl:flex-row gap-[20px]">
              <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] xl:w-[392px] min-[800px]:bg-[#F3F5F9] min-[800px]:p-8 min-[800px]:mb-0">
                <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center min-[800px]:text-[16px]">Journal Quartiles</h3>

                <JournalQuartiles />
              </div>

              <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] xl:w-[calc(100%-402px)] min-[800px]:bg-[#F3F5F9] min-[800px]:p-8 min-[800px]:mb-0">
                <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center">
                  Top 5 most published categories
                  <br className="min-[800px]:hidden" />by DB in PNU
                </h3>
                <TopCategoriesByDB />
              </div>

              <div className="flex justify-center min-[800px]:hidden">
                <button
                  className="flex items-center gap-[2px] px-[10px] py-1 text-[13px] font-semibold text-[#8D9AAF] cursor-pointer"
                  onClick={() => setGraphExpanded(false)}
                >
                  <img src={`${rmVars.themeUrl}images/expand_more_8D9AAF.png`} className="w-[14px] rotate-180" alt="hide" />
                  Hide Chart
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="listUtil mt-10 min-[800px]:flex items-center justify-between">
          <div className="mb-3 text-[14px] text-[#6C7687]">
            Found <strong>{(pageInfo.totalResults && Number(pageInfo.totalResults).toLocaleString()) || 0}</strong> results
          </div>

          <JournalSortSelect
            sort={sort}
            onChange={(nextSort) => {
              setSort(nextSort);
              syncUrl({ page: 1, sort: nextSort });
              // 새로운 sort 값을 직접 사용하여 extra 파라미터 생성
              const extra = buildExtraParams();
              if (nextSort?.order_by) extra.order_by = nextSort.order_by;
              if (nextSort?.order) extra.order = nextSort.order;
              fetchList(1, { extra });
            }}
          />
        </div>

        <div className="relative mt-4 min-[800px]:mt-3">
          {loading && (
            <ChartSkeletonLoader show={loading} />
          )}
          {rows.length !== 0 && (<ul className="journal-list-wrap flex flex-col gap-3">
            {rows.map((r) => (<JournalCard key={r.journal_id || r.id || r.issn || Math.random()} item={r} highlightQuery={query} />))}
          </ul>)}

          {rows.length === 0 && !loading && (<div className="no-journal-list">
            <div className="text-[13px] font-semibold text-center bg-white py-[72px] rounded-xl">
              No results found
            </div>
          </div>)}
        </div>

        <Paginator pageInfo={pageInfo} onPage={onPage} />

        <FacetPopup
          open={!!popup.open}
          title={popup.title}
          items={arr(facet?.[popup.key]?.result)}
          selected={pendingFilters?.[popup.key] || []}
          facetKey={popup.key}
          conjunction={popup.conjunction}
          conjunctionValue={popup.conjunctionValue}
          onConjunctionChange={popup.onConjunctionChange}
          onToggle={(value) => {
            const k = popup.key;
            if (!k) return;
            setPendingFilters(prev => {
              const prevArr = Array.isArray(prev[k]) ? prev[k] : [];
              const exists = prevArr.includes(value);
              const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
              return { ...prev, [k]: nextArr };
            });
          }}
          onRefine={() => {
            const next = cleanFilters(pendingFilters || {});
            setFilters(next);
            setPendingFilters(next);
            // 그래프 닫기
            setGraphExpanded(false);
            const extra = {};
            Object.entries(next).forEach(([k, vals]) => {
              if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
            });
            // conjunction 파라미터 추가
            Object.entries(conjunctions).forEach(([k, conj]) => {
              if (conj === 'and') {
                extra[`${k}_conjunction`] = 'and';
              }
            });
            if (sort?.order_by) extra.order_by = sort.order_by;
            if (sort?.order) extra.order = sort.order;
            if (String(query || '').trim()) extra.sub_ks = String(query).trim();
            syncUrl({ page: 1, filters: next, sort, conjunctions });
            try {
              if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            } catch (e) {
            }
            setPopup(p => ({ ...p, open: false }));
            fetchList(1, { extra });
          }}
          onCancel={() => setPopup(p => ({ ...p, open: false }))}
        />
      </div>
    </div>);
}

//current_page=2&record_per_page=30&ks=&sub_ks=&registered_db_name[]=Nature%20Index&registered_db_name[]=SCIE&registered_db_name[]=Scopus&registered_db_name[]=ESCI&journal_ranking=&order_by=ranking&order=asc&date_type=input_date&in_date_start=&in_date_end=&worker_name=